// Pure rules for report widgets: which forms a saved report can take, whether a
// stored widget still fits its report, and the one total a Number widget shows.
import type { ResultRow } from './arrowToRows';
import { splitMeasure, sumAdditiveColumns } from './chartFormat';
import { resolveColumnMeta, type RelatedManifests } from './columnRefs';
import { measureAlias } from './compileReport';
import { humanizeHeader } from './formatValue';
import type {
  DatasetManifestEntry,
  ReportDefinition,
  ReportWidget,
  WidgetForm,
} from '../types/reporting';

export const WIDGET_FORMS: readonly WidgetForm[] = [
  'number',
  'bar',
  'line',
  'pie',
  'table',
];

export const WIDGET_FORM_LABELS: Record<WidgetForm, string> = {
  number: 'Number',
  bar: 'Bar chart',
  line: 'Line chart',
  pie: 'Pie chart',
  table: 'Table',
};

export const TABLE_ROW_LIMIT = { default: 5, min: 5, max: 25 } as const;

export function additiveMeasureAliases(def: ReportDefinition): string[] {
  return def.measures
    .filter(m => m.agg === 'count' || m.agg === 'sum')
    .map(measureAlias);
}

export function widgetFormReasons(
  def: ReportDefinition,
  dataset: DatasetManifestEntry,
  related: RelatedManifests
): Record<WidgetForm, string | null> {
  const pivoted = !!def.pivot_column;
  if (def.measures.length === 0 || (def.group_bys.length === 0 && !pivoted)) {
    const reason = 'Only grouped or pivot reports can become widgets.';
    return {
      number: reason,
      bar: reason,
      line: reason,
      pie: reason,
      table: reason,
    };
  }
  const additive = additiveMeasureAliases(def).length > 0;
  const noPivot = pivoted ? 'Not available for pivot reports.' : null;
  const groupBy = def.group_bys.length === 1 ? def.group_bys[0] : null;
  const groupMeta = groupBy
    ? resolveColumnMeta(groupBy.column, dataset, related)
    : undefined;
  const dateGrouping =
    groupMeta?.type === 'date' &&
    (!groupBy?.transform || groupBy.transform.type === 'date_trunc');
  const needsAdditive = additive ? null : 'Needs a count or sum value.';
  return {
    number: needsAdditive,
    bar: noPivot,
    line: noPivot ?? (dateGrouping ? null : 'Needs one date grouping.'),
    pie: noPivot ?? (groupBy ? needsAdditive : 'Needs exactly one grouping.'),
    table: null,
  };
}

/** Why the widget cannot show this report, or null when it can. */
export function widgetMismatchReason(
  widget: Pick<ReportWidget, 'name' | 'form' | 'display'>,
  def: ReportDefinition,
  dataset: DatasetManifestEntry,
  related: RelatedManifests
): string | null {
  const reason = widgetFormReasons(def, dataset, related)[widget.form];
  if (reason)
    return `The widget “${widget.name}” (${WIDGET_FORM_LABELS[widget.form]}) no longer fits this report. ${reason}`;
  if (widget.form !== 'number' && widget.form !== 'pie') return null;
  const additive = additiveMeasureAliases(def);
  const { measure, subtext_measure: subtext } = widget.display;
  const wanted = widget.form === 'number' ? [measure, subtext] : [measure];
  const missing = !measure
    ? ''
    : wanted.find(alias => alias && !additive.includes(alias));
  if (missing == null) return null;
  return `The widget “${widget.name}” shows ${humanizeHeader(missing)}, which this report no longer has. Add it back, or change the widget first.`;
}

export function widgetFitsReport(
  widget: Pick<ReportWidget, 'name' | 'form' | 'display'>,
  def: ReportDefinition,
  dataset: DatasetManifestEntry,
  related: RelatedManifests
): boolean {
  return widgetMismatchReason(widget, def, dataset, related) === null;
}

export function measureTotal(
  columns: string[],
  rows: ResultRow[],
  dimensionKeys: string[],
  alias: string
): number | null {
  const sums = sumAdditiveColumns(rows, columns, new Set(dimensionKeys));
  const cells = columns
    .filter(column => splitMeasure(column).alias === alias)
    .map(column => sums[column])
    .filter((value): value is number => value !== null);
  return cells.length > 0 ? cells.reduce((a, b) => a + b, 0) : null;
}

// A Line widget reads left to right in time. The stored report may sort newest
// first, so order the points by the date group-by here without touching the
// stored definition.
export function oldestFirst(rows: ResultRow[], dimension: string): ResultRow[] {
  const time = (row: ResultRow): number => {
    const value = row[dimension];
    const ms =
      value instanceof Date ? value.getTime() : Date.parse(String(value));
    return Number.isNaN(ms) ? 0 : ms;
  };
  return [...rows].sort((a, b) => time(a) - time(b));
}

/** The report's relative date window, as "Visit date: last 30 days". */
export function widgetPeriod(
  def: ReportDefinition,
  dataset: DatasetManifestEntry,
  related: RelatedManifests
): string | null {
  for (const filter of def.filters) {
    if (filter.op !== 'relative_date') continue;
    const value = filter.value as { last_days?: number; next_days?: number };
    const label =
      resolveColumnMeta(filter.column, dataset, related)?.label ??
      filter.column;
    if (value?.last_days) return `${label}: last ${value.last_days} days`;
    if (value?.next_days) return `${label}: next ${value.next_days} days`;
  }
  return null;
}

/** The builder page behind a widget. */
export function widgetReportHref(widget: ReportWidget): string {
  return widget.system_report_key
    ? `/reports/builder?system=${widget.system_report_key}`
    : `/reports/builder?id=${widget.report_definition_id}`;
}

/** The builder page that edits a widget's report and then returns to `returnTo`. */
export function widgetEditReportHref(
  widget: ReportWidget,
  returnTo: string
): string {
  const params = new URLSearchParams({
    widget: widget.id,
    return_to: returnTo,
  });
  return `${widgetReportHref(widget)}&${params}`;
}

/** `value` when it is a dashboard path in this app, else null. */
export function safeDashboardReturnTo(value: string | null): string | null {
  return value && /^\/dashboard(?:[?#]|$)/.test(value) ? value : null;
}
